<script setup lang="ts">
import { computed, ref } from 'vue';
import DatePicker from 'primevue/datepicker';
import { useDropdownState } from 'src/composables/useDropdownState';
import SharedDropdown from 'src/components/shared/SharedDropdown.vue';
import 'src/assets/ticket-edit-property.css';

const props = defineProps<{
  scheduledDate?: Date;
}>();

const emit = defineEmits<{
  'update:scheduledDate': [date: Date | null];
}>();

const dateEl = ref<HTMLElement>();
const dateDropdown = useDropdownState(dateEl);

const timeEl = ref<HTMLElement>();
const timeDropdown = useDropdownState(timeEl);

const hours = Array.from({ length: 24 }, (_, i) => i);
const minutes = [0, 15, 30, 45];

const formattedDate = computed(() => {
  if (!props.scheduledDate) return '—';
  return props.scheduledDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
});

const formattedTime = computed(() => {
  if (!props.scheduledDate) return '—';
  const h = props.scheduledDate.getHours().toString().padStart(2, '0');
  const m = props.scheduledDate.getMinutes().toString().padStart(2, '0');
  return `${h}:${m}`;
});

function clearDate(): void {
  emit('update:scheduledDate', null);
  dateDropdown.close();
}

function clearTime(): void {
  emit('update:scheduledDate', null);
  timeDropdown.close();
}

function handleDateSelect(date: Date): void {
  const current = props.scheduledDate ? new Date(props.scheduledDate) : new Date();
  current.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
  emit('update:scheduledDate', current);
  dateDropdown.close();
}

function selectTimeHour(h: number): void {
  const current = props.scheduledDate ? new Date(props.scheduledDate) : new Date();
  current.setHours(h);
  emit('update:scheduledDate', current);
}

function selectTimeMinute(m: number): void {
  const current = props.scheduledDate ? new Date(props.scheduledDate) : new Date();
  current.setMinutes(m);
  emit('update:scheduledDate', current);
  timeDropdown.close();
}
</script>

<template>
  <div ref="dateEl" class="relative">
    <div class="prop-row" @click="dateDropdown.toggle">
      <span class="prop-label">Date</span>
      <span class="text-s font-medium text-surface-700 dark:text-surface-300 flex items-center gap-1.5">
        <i class="pi pi-calendar" style="font-size: 11px; opacity: 0.5" />
        {{ formattedDate }}
      </span>
    </div>
    <SharedDropdown :open="dateDropdown.isOpen.value" min-width="260px" max-height="none" :show-clear="!!scheduledDate" clear-label="Clear scheduled date and time" @clear="clearDate" @click.stop>
      <DatePicker
        :model-value="scheduledDate"
        inline
        @date-select="handleDateSelect"
        size="small"
      />
    </SharedDropdown>
  </div>

  <div ref="timeEl" class="relative">
    <div class="prop-row" @click="timeDropdown.toggle">
      <span class="prop-label">Time</span>
      <span class="text-s font-medium text-surface-700 dark:text-surface-300 font-mono tracking-wide">
        {{ formattedTime }}
      </span>
    </div>
    <SharedDropdown :open="timeDropdown.isOpen.value" min-width="180px" :show-clear="!!scheduledDate" clear-label="Clear scheduled date and time" @clear="clearTime" @click.stop>
      <div class="flex gap-1 p-1.5">
        <div class="flex-1 max-h-[200px] overflow-y-auto">
          <div class="text-[10px] font-semibold text-surface-400 uppercase text-center mb-1">Hour</div>
          <button
            v-for="h in hours"
            :key="h"
            class="te-dropdown-item !justify-center !py-1 text-xs font-mono"
            :class="{ 'te-dropdown-item-active': scheduledDate && scheduledDate.getHours() === h }"
            @click.stop="selectTimeHour(h)"
          >
            {{ h.toString().padStart(2, '0') }}
          </button>
        </div>
        <div class="w-px bg-surface-200 dark:bg-surface-700 my-2" />
        <div class="flex-1">
          <div class="text-[10px] font-semibold text-surface-400 uppercase text-center mb-1">Min</div>
          <button
            v-for="m in minutes"
            :key="m"
            class="te-dropdown-item !justify-center !py-1 text-xs font-mono"
            :class="{ 'te-dropdown-item-active': scheduledDate && Math.floor(scheduledDate.getMinutes() / 15) * 15 === m }"
            @click.stop="selectTimeMinute(m)"
          >
            {{ m.toString().padStart(2, '0') }}
          </button>
        </div>
      </div>
    </SharedDropdown>
  </div>
</template>
